<template>
    <b-container v-if="wizardData" fluid>
        <b-row class="mt-3 no-gutters">
            <b-col cols="auto" class="mr-auto">
                <b>{{ wizardData.name }} - Address</b>
            </b-col>
            <b-col v-if="addressesOptions" cols="auto" class="mr-2">
                <form-select class="w-100" size="md" v-model="selectedAddress" :options="addressesOptions" />
            </b-col>
            <b-col v-if="addressesOptions" cols="auto">
                <b-button variant="primary" size="md" @click="fillAddress(selectedAddress)"> Fill Address </b-button>
            </b-col>
        </b-row>
        <hr />
        <validation-observer ref="address_form" v-slot="{ meta, handleSubmit }">
            {{ setInvalid(!meta.valid) }}
            <b-form @submit.prevent="save">
                <b-row>
                    <b-col class="col-12 col-md-6">
                        <form-input
                            floatingLabel="Address 1"
                            :leftIcon="['fas', 'location-dot']"
                            v-model="addressData.address.address1"
                            @change="geoCodeAddressMovePoint"
                            @keydown.enter="save"
                        />
                    </b-col>
                    <b-col class="col-12 col-md-6">
                        <form-input
                            floatingLabel="Address 2"
                            :leftIcon="['fas', 'location-dot']"
                            v-model="addressData.address.address2"
                            @change="geoCodeAddressMovePoint"
                            @keydown.enter="handleSubmit(_, save)"
                        />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col class="col-12 col-md-6 col-lg-4">
                        <form-select
                            floatingLabel="Country"
                            v-model="addressData.address.country.id"
                            :options="countriesOptions"
                            @change="geoCodeAddressMovePoint"
                            :leftIcon="['fas', 'location-dot']"
                        />
                    </b-col>
                    <b-col class="col-12 col-md-6 col-lg-2">
                        <validation-provider name="address.zip" v-slot="{ errors }" rules="required">
                            <form-input
                                floatingLabel="Zip"
                                :leftIcon="['fas', 'location-dot']"
                                v-model="addressData.address.zip"
                                @input="fillFromZip"
                                @keydown.enter="handleSubmit(_, save)"
                                :error="errors[0]"
                            />
                        </validation-provider>
                    </b-col>
                    <b-col class="col-12 col-md-12 col-lg-6 mb-3">
                        Your city and state will be automatically added
                    </b-col>
                </b-row>
                <b-row>
                    <b-col class="col-12 col-md-6">
                        <validation-provider name="address.city" v-slot="{ errors }" rules="required" >
                            <form-input
                                floatingLabel="City"
                                :leftIcon="['fas', 'location-dot']"
                                v-model="addressData.address.city"
                                @keydown.enter="handleSubmit(_, save)"
                                :error="errors[0]"
                            />
                        </validation-provider>
                    </b-col>
                    <b-col class="col-12 col-md-6">
                        <validation-provider name="address.state" v-slot="{ errors }" rules="required" >
                            <form-select
                                :leftIcon="['fas', 'location-dot']"
                                floatingLabel="State"
                                v-model="addressData.address.state.id"
                                :options="statesOptions"
                                @change="geoCodeAddressMovePoint"
                                :error="errors[0]"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col class="col-12 col-md-6">
                        <validation-provider name="address.phone" rules="validPhoneNumber" v-slot="{ errors }">
                            <form-phone-number-input
                                v-if="isMounted"
                                inputId="phone"
                                floating-label="Phone"
                                v-model="addressData.address.phone"
                                :default-country-code="defaultCountryCode"
                                :error="errors[0]"
                                @validate="onValidatePhone"
                                @input="setPhone"
                            />
                        </validation-provider>
                    </b-col>
                    <b-col class="col-12 col-md-6">
                        <form-select
                            v-if="isMounted"
                            v-model="addressData.area"
                            floatingLabel="Category"
                            :options="categoryOptions"
                            :leftIcon="['fas', 'sitemap']"
                        />
                    </b-col>
                </b-row>
                <b-row class="mt-2 mb-1">
                    <b-col>
                        <h6>Adjust coordinates by changing the address above or double clicking on the map below.</h6>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col class="col-12 col-sm-6">
                        <validation-provider
                            name="address.latitude"
                            v-slot="{ errors }"
                            rules="validLatitude"
                            
                        >
                            <form-input
                                floatingLabel="Latitude"
                                :leftIcon="['fas', 'earth-americas']"
                                v-model="addressData.address.latitude"
                                @input="movePoint"
                                @keydown.enter="handleSubmit(_, save)"
                                :error="errors[0]"
                            />
                        </validation-provider>
                    </b-col>
                    <b-col class="col-12 col-sm-6">
                        <validation-provider
                            name="address.longitude"
                            v-slot="{ errors }"
                            rules="validLongitude"
                            
                        >
                            <form-input
                                floatingLabel="Longitude"
                                :leftIcon="['fas', 'earth-americas']"
                                v-model="addressData.address.longitude"
                                @input="movePoint"
                                @keydown.enter="handleSubmit(_, save)"
                                :error="errors[0]"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row class="mb-3">
                    <b-col>
                        <div class="google-map" ref="googleMap"></div>
                    </b-col>
                </b-row>
                <b-row class="mt-5 mb-3">
                    <b-col>
                        <h6>Location Settings (Airbnb Only)</h6>
                    </b-col>
                </b-row>
                <hr />
                <b-row>
                    <b-col md="6">
                        <form-select
                            v-model="addressData.airbnb_location.show_exact_location"
                            floatingLabel="Show Exact Location"
                            :options="showExactLocationOptions"
                            :leftIcon="['fas', 'map-pin']"
                        />
                    </b-col>
                </b-row>
                <b-row class="mt-4 mb-3">
                    <b-col>
                        <h6 class="expandable" @click="changeShowNearby">
                            Nearby Places and Activities (VRBO Only)
                            <font-awesome-icon v-if="!showNearby" :icon="['fas', 'circle-plus']" />
                            <font-awesome-icon v-if="showNearby" :icon="['fas', 'circle-minus']" />
                        </h6>
                    </b-col>
                </b-row>
                <hr />
                <span v-if="showNearby">
                    <b-row class="mb-3 mb-xl-1">
                        <b-col md="6">
                            <form-select
                                v-model="addressData.locations.show_exact_location"
                                floatingLabel="Show Exact Location"
                                :options="showExactLocationOptions"
                                :leftIcon="['fas', 'map-pin']"
                            />
                        </b-col>
                        <b-col md="6">
                            <form-select
                                v-model="addressData.locations.car"
                                floatingLabel="Car"
                                :options="carOptions"
                                :leftIcon="['fas', 'car']"
                            />
                        </b-col>
                    </b-row>
                    <b-row class="mb-3 mb-xl-1">
                        <b-col class="col-12 col-xl-4">
                            <form-input
                                v-model="addressData.locations.nearest_airport"
                                floatingLabel="Nearest Airport"
                                :leftIcon="['fas', 'road']"
                                @keydown.enter="handleSubmit(_, save)"
                            />
                        </b-col>
                        <b-col class="col-12 col-xl-1">
                            <validation-provider
                                name="locations.nearest_airport_distance"
                                v-slot="{ errors }"
                                rules="validFloat"
                                
                            >
                                <form-input
                                    floatingLabel="Distance"
                                    v-model="addressData.locations.nearest_airport_distance"
                                    @keydown.enter="handleSubmit(_, save)"
                                    :error="errors[0]"
                                />
                            </validation-provider>
                        </b-col>
                        <b-col class="col-12 col-xl-1 mb-3 mb-xl-1">
                            <form-select
                                floatingLabel="Unit"
                                v-model="addressData.locations.nearest_airport_distance_unit"
                                :options="distanceUnitOptions"
                            />
                        </b-col>
                        <b-col class="col-12 col-xl-4">
                            <form-input
                                v-model="addressData.locations.nearest_beach"
                                floatingLabel="Nearest Beach"
                                :leftIcon="['fas', 'road']"
                                @keydown.enter="handleSubmit(_, save)"
                            />
                        </b-col>
                        <b-col class="col-12 col-xl-1">
                            <validation-provider
                                name="locations.nearest_beach_distance"
                                v-slot="{ errors }"
                                rules="validFloat"
                                
                            >
                                <form-input
                                    floatingLabel="Distance"
                                    v-model="addressData.locations.nearest_beach_distance"
                                    @keydown.enter="handleSubmit(_, save)"
                                    :error="errors[0]"
                                />
                            </validation-provider>
                        </b-col>
                        <b-col class="col-12 col-xl-1">
                            <form-select
                                floatingLabel="Unit"
                                v-model="addressData.locations.nearest_beach_distance_unit"
                                :options="distanceUnitOptions"
                            />
                        </b-col>
                    </b-row>
                    <b-row class="mb-3 mb-xl-1">
                        <b-col class="col-12 col-xl-4">
                            <form-input
                                v-model="addressData.locations.nearest_ferry"
                                floatingLabel="Nearest Ferry"
                                :leftIcon="['fas', 'road']"
                                @keydown.enter="handleSubmit(_, save)"
                            />
                        </b-col>
                        <b-col class="col-12 col-xl-1">
                            <validation-provider
                                name="locations.nearest_ferry_distance"
                                v-slot="{ errors }"
                                rules="validFloat"
                                
                            >
                                <form-input
                                    floatingLabel="Distance"
                                    v-model="addressData.locations.nearest_ferry_distance"
                                    @keydown.enter="handleSubmit(_, save)"
                                    :error="errors[0]"
                                />
                            </validation-provider>
                        </b-col>
                        <b-col class="col-12 col-xl-1 mb-3 mb-xl-1">
                            <form-select
                                floatingLabel="Unit"
                                v-model="addressData.locations.nearest_ferry_distance_unit"
                                :options="distanceUnitOptions"
                            />
                        </b-col>
                        <b-col class="col-12 col-xl-4">
                            <form-input
                                v-model="addressData.locations.nearest_train"
                                floatingLabel="Nearest Train"
                                :leftIcon="['fas', 'road']"
                                @keydown.enter="handleSubmit(_, save)"
                            />
                        </b-col>
                        <b-col class="col-12 col-xl-1">
                            <validation-provider
                                name="locations.nearest_train_distance"
                                v-slot="{ errors }"
                                rules="validFloat"
                                
                            >
                                <form-input
                                    floatingLabel="Distance"
                                    v-model="addressData.locations.nearest_train_distance"
                                    @keydown.enter="handleSubmit(_, save)"
                                    :error="errors[0]"
                                />
                            </validation-provider>
                        </b-col>
                        <b-col class="col-12 col-xl-1">
                            <form-select
                                floatingLabel="Unit"
                                v-model="addressData.locations.nearest_train_distance_unit"
                                :options="distanceUnitOptions"
                            />
                        </b-col>
                    </b-row>
                    <b-row class="mb-3 mb-xl-1">
                        <b-col class="col-12 col-xl-4">
                            <form-input
                                v-model="addressData.locations.nearest_highway"
                                floatingLabel="Nearest Highway"
                                :leftIcon="['fas', 'road']"
                                @keydown.enter="handleSubmit(_, save)"
                            />
                        </b-col>
                        <b-col class="col-12 col-xl-1">
                            <validation-provider
                                name="locations.nearest_highway_distance"
                                v-slot="{ errors }"
                                rules="validFloat"
                                
                            >
                                <form-input
                                    floatingLabel="Distance"
                                    v-model="addressData.locations.nearest_highway_distance"
                                    @keydown.enter="handleSubmit(_, save)"
                                    :error="errors[0]"
                                />
                            </validation-provider>
                        </b-col>
                        <b-col class="col-12 col-xl-1 mb-3 mb-xl-1">
                            <form-select
                                floatingLabel="Unit"
                                v-model="addressData.locations.nearest_highway_distance_unit"
                                :options="distanceUnitOptions"
                            />
                        </b-col>
                        <b-col class="col-12 col-xl-4">
                            <form-input
                                v-model="addressData.locations.nearest_bar"
                                floatingLabel="Nearest Bar"
                                :leftIcon="['fas', 'road']"
                                @keydown.enter="handleSubmit(_, save)"
                            />
                        </b-col>
                        <b-col class="col-12 col-xl-1">
                            <validation-provider
                                name="locations.nearest_bar_distance"
                                v-slot="{ errors }"
                                rules="validFloat"
                                
                            >
                                <form-input
                                    floatingLabel="Distance"
                                    v-model="addressData.locations.nearest_bar_distance"
                                    @keydown.enter="handleSubmit(_, save)"
                                    :error="errors[0]"
                                />
                            </validation-provider>
                        </b-col>
                        <b-col class="col-12 col-xl-1">
                            <form-select
                                floatingLabel="Unit"
                                v-model="addressData.locations.nearest_bar_distance_unit"
                                :options="distanceUnitOptions"
                            />
                        </b-col>
                    </b-row>
                    <b-row class="mb-3 mb-xl-1">
                        <b-col class="col-12 col-xl-4">
                            <form-input
                                v-model="addressData.locations.nearest_ski"
                                floatingLabel="Nearest Ski"
                                :leftIcon="['fas', 'road']"
                                @keydown.enter="handleSubmit(_, save)"
                            />
                        </b-col>
                        <b-col class="col-12 col-xl-1">
                            <validation-provider
                                name="locations.nearest_ski_distance"
                                v-slot="{ errors }"
                                rules="validFloat"
                                
                            >
                                <form-input
                                    floatingLabel="Distance"
                                    v-model="addressData.locations.nearest_ski_distance"
                                    @keydown.enter="handleSubmit(_, save)"
                                    :error="errors[0]"
                                />
                            </validation-provider>
                        </b-col>
                        <b-col class="col-12 col-xl-1 mb-3 mb-xl-1">
                            <form-select
                                floatingLabel="Unit"
                                v-model="addressData.locations.nearest_ski_distance_unit"
                                :options="distanceUnitOptions"
                            />
                        </b-col>
                        <b-col class="col-12 col-xl-4">
                            <form-input
                                v-model="addressData.locations.nearest_golf"
                                floatingLabel="Nearest Golf"
                                :leftIcon="['fas', 'road']"
                                @keydown.enter="handleSubmit(_, save)"
                            />
                        </b-col>
                        <b-col class="col-12 col-xl-1">
                            <validation-provider
                                name="locations.nearest_golf_distance"
                                v-slot="{ errors }"
                                rules="validFloat"
                                
                            >
                                <form-input
                                    floatingLabel="Distance"
                                    v-model="addressData.locations.nearest_golf_distance"
                                    @keydown.enter="handleSubmit(_, save)"
                                    :error="errors[0]"
                                />
                            </validation-provider>
                        </b-col>
                        <b-col class="col-12 col-xl-1">
                            <form-select
                                floatingLabel="Unit"
                                v-model="addressData.locations.nearest_golf_distance_unit"
                                :options="distanceUnitOptions"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col class="col-12 col-xl-4">
                            <form-input
                                v-model="addressData.locations.nearest_restaurant"
                                floatingLabel="Nearest Restaurant"
                                :leftIcon="['fas', 'road']"
                                @keydown.enter="handleSubmit(_, save)"
                            />
                        </b-col>
                        <b-col class="col-12 col-xl-1">
                            <validation-provider
                                name="locations.nearest_restaurant_distance"
                                v-slot="{ errors }"
                                rules="validFloat"
                                
                            >
                                <form-input
                                    floatingLabel="Distance"
                                    v-model="addressData.locations.nearest_restaurant_distance"
                                    @keydown.enter="handleSubmit(_, save)"
                                    :error="errors[0]"
                                />
                            </validation-provider>
                        </b-col>
                        <b-col class="col-12 col-xl-1">
                            <form-select
                                floatingLabel="Unit"
                                v-model="addressData.locations.nearest_restaurant_distance_unit"
                                :options="distanceUnitOptions"
                            />
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col>
                            <b-button class="" variant="outline-primary" @click="$bvModal.show('copy-locations-modal')">
                                <font-awesome-icon :icon="['fas', 'copy']" />
                                Copy Locations
                            </b-button>
                        </b-col>
                    </b-row>
                </span>
                <b-row class="mt-5 mb-3">
                    <b-col>
                        <h6 class="expandable" @click="changeShowStory">
                            Owner Story (VRBO Only)
                            <font-awesome-icon v-if="!showStory" :icon="['fas', 'circle-plus']" />
                            <font-awesome-icon v-if="showStory" :icon="['fas', 'circle-minus']" />
                        </h6>
                    </b-col>
                </b-row>
                <hr />
                <b-row v-if="showStory">
                    <b-col class="col-12 col-lg-6">
                        <b-row>
                            <b-col class="col-12" style="margin-bottom: 11px">
                                <validation-provider
                                    name="ownerstory.year_purchased"
                                    v-slot="{ errors }"
                                    rules="validPositiveInteger"
                                    
                                >
                                    <form-input
                                        v-model="addressData.ownerstory.year_purchased"
                                        floatingLabel="Year Purchased"
                                        :leftIcon="['fas', 'calendar-days']"
                                        @keydown.enter="handleSubmit(_, save)"
                                        :error="errors[0]"
                                    />
                                </validation-provider>
                            </b-col>
                            <b-col class="col-12">
                                <form-textarea
                                    v-model="addressData.ownerstory.why_purchased"
                                    floating-label="Why This Property?"
                                    :left-icon="['fas', 'note-sticky']"
                                    rows="4"
                                />
                            </b-col>
                        </b-row>
                    </b-col>
                    <b-col class="col-12 col-lg-6">
                        <form-textarea
                            v-model="addressData.ownerstory.owner_listing_story"
                            floating-label="About you?"
                            :left-icon="['fas', 'note-sticky']"
                            rows="7"
                        />
                    </b-col>
                </b-row>
            </b-form>
            <b-button class="mt-1" variant="primary" @click="save" :disabled="!meta.valid"> Save </b-button>
        </validation-observer>
        <copy-locations-modal @set-locations="setLocations" />
    </b-container>
</template>

<script>
    import API from '../../../../api';
    import { mapGetters } from 'vuex';
    import isNil from 'lodash/isNil';
    import isEmpty from 'lodash/isEmpty';
    import debounce from 'lodash/debounce';
    import { parsePhoneNumber } from 'awesome-phonenumber';
    import GoogleMapsApiLoader from 'google-maps-api-loader';
    import CopyLocationsModal from './copy-locations-modal.vue';
    import FormInput from '../../../generic/form-input/form-input.vue';
    import FormSelect from '../../../generic/form-select/form-select.vue';
    import FormTextarea from '../../../generic/form-textarea/form-textarea.vue';
    import FormPhoneNumberInput from '../../../generic/form-phone-number-input/form-phone-number-input.vue';
    import { RENTAL_PROPERTY_SETUP_ADDRESS_ENDPOINT } from '../../../../endpoints';
    import { removeScrollMixin } from '../../../../mixins/remove-scroll-mixin';
    import { formatResponseError } from '../../../../utils/api';
    import { distanceUnitChoice } from '../constants';

    export default {
        components: {
            FormInput,
            FormSelect,
            FormTextarea,
            CopyLocationsModal,
            FormPhoneNumberInput,
        },
        mixins: [removeScrollMixin],
        inject: ['propertyId', 'getModuleEndpointAndBody'],
        data() {
            return {
                google: null,
                map: null,
                markers: [],
                addressData: this.getAddressDataValue({}),
                showExactLocationOptions: [
                    { value: false, text: 'No' },
                    { value: true, text: 'Yes' },
                ],
                carOptions: [
                    { value: 0, text: 'Necessary' },
                    { value: 1, text: 'Not necessary' },
                    { value: 2, text: 'Recommended' },
                ],
                distanceUnitOptions: distanceUnitChoice,
                selectedAddress: null,
                showNearby: true,
                showStory: false,
                isMounted: false,
            };
        },
        async mounted() {
            const googleMapApi = await GoogleMapsApiLoader({
                apiKey: window.GOOGLE_MAPS_KEY,
            });
            this.google = googleMapApi;
            await this.initializeMap();
            await this.fetchAddress();
            this.isMounted = true;
            this.setAddress();
        },
        computed: {
            ...mapGetters('setupPropertyStore', [
                'address',
                'wizardData',
                'initialData',
                'addressesOptions',
                'countriesOptions',
                'categoryOptions',
                'statesOptions',
                'countries',
                'states',
            ]),
            defaultCountryCode() {
                if (this.addressData.address.phone) {
                    const phone = parsePhoneNumber(this.addressData.address.phone);
                    return phone.g.regionCode;
                } else {
                    return this.$store.getters['account/code'];
                }
            },
        },
        methods: {
            async initializeMap() {
                const mapContainer = this.$refs.googleMap;
                this.map = new this.google.maps.Map(mapContainer, {
                    center: { lat: 28, lng: -81 },
                    zoom: 10,
                    gestureHandling: 'cooperative',
                    disableDoubleClickZoom: true,
                });
            },
            getAddressDataValue(addressData) {
                return {
                    area: addressData.area || 0,
                    address: this.getAddressValue(addressData.address || {}),
                    locations: this.getLocationsValue(addressData.locations || {}),
                    ownerstory: this.getOwnerStoryValue(addressData.ownerstory || {}),
                    airbnb_location: addressData.airbnb_location || {
                        show_exact_location: false,
                    },
                };
            },
            getAddressValue(address) {
                return {
                    address1: address.address1 || '',
                    address2: address.address2 || '',
                    country: address.country || { id: '', name: '', code: '' },
                    state: address.state || { code: '', id: '', name: '' },
                    latitude: !isNil(address.latitude) ? parseFloat(address.latitude) : 0,
                    longitude: !isNil(address.longitude) ? parseFloat(address.longitude) : 0,
                    city: address.city || '',
                    zip: address.zip || '',
                    phone: address.phone || '',
                };
            },
            getLocationsValue(locations) {
                return {
                    show_exact_location: !isNil(locations.show_exact_location) ? locations.show_exact_location : true,
                    nearest_airport: locations.nearest_airport || '',
                    nearest_airport_distance: locations.nearest_airport_distance || '',
                    nearest_airport_distance_unit: locations.nearest_airport_distance_unit || '',
                    nearest_beach: locations.nearest_beach || '',
                    nearest_beach_distance: locations.nearest_beach_distance || '',
                    nearest_beach_distance_unit: locations.nearest_beach_distance_unit || '',
                    nearest_ferry: locations.nearest_ferry || '',
                    nearest_ferry_distance: locations.nearest_ferry_distance || '',
                    nearest_ferry_distance_unit: locations.nearest_ferry_distance_unit || '',
                    nearest_train: locations.nearest_train || '',
                    nearest_train_distance: locations.nearest_train_distance || '',
                    nearest_train_distance_unit: locations.nearest_train_distance_unit || '',
                    nearest_highway: locations.nearest_highway || '',
                    nearest_highway_distance: locations.nearest_highway_distance || '',
                    nearest_highway_distance_unit: locations.nearest_highway_distance_unit || '',
                    nearest_bar: locations.nearest_bar || '',
                    nearest_bar_distance: locations.nearest_bar_distance || '',
                    nearest_bar_distance_unit: locations.nearest_bar_distance_unit || '',
                    nearest_ski: locations.nearest_ski || '',
                    nearest_ski_distance: locations.nearest_ski_distance || '',
                    nearest_ski_distance_unit: locations.nearest_ski_distance_unit || '',
                    nearest_golf: locations.nearest_golf || '',
                    nearest_golf_distance: locations.nearest_golf_distance || '',
                    nearest_golf_distance_unit: locations.nearest_golf_distance_unit || '',
                    nearest_restaurant: locations.nearest_restaurant || '',
                    nearest_restaurant_distance: locations.nearest_restaurant_distance || '',
                    nearest_restaurant_distance_unit: locations.nearest_restaurant_distance_unit || '',
                    car: !isNil(locations.car) ? parseInt(locations.car) : null,
                };
            },
            getOwnerStoryValue(ownerstory) {
                return {
                    year_purchased: ownerstory.year_purchased || null,
                    why_purchased: ownerstory.why_purchased || '',
                    owner_listing_story: ownerstory.owner_listing_story || '',
                };
            },
            async fetchAddress() {
                try {
                    this.$parent.$emit('busy', true);
                    if (!this.address) {
                        const data = await this.$store.dispatch('setupPropertyStore/getAddress', {
                            propertyId: this.propertyId,
                        });
                        this.addressData = this.getAddressDataValue(data);
                    } else {
                        this.addressData = this.getAddressDataValue(this.address);
                    }
                    this.commitChangesToStore(this.addressData);
                    if (this.initialData?.addresses?.length) this.selectedAddress = this.initialData.addresses[0][0];
                    this.$refs.address_form.setValues(this.addressData);
                } catch (e) {
                    this.errorToast({ text: 'Could not load address.' });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            setAddress() {
                const marker = new this.google.maps.Marker({
                    zIndex: 1,
                    position: {
                        lat: this.addressData.address.latitude,
                        lng: this.addressData.address.longitude,
                    },
                    map: this.map,
                    draggable: false,
                });
                this.markers.push(marker);
                this.map.panTo(marker.getPosition());
                this.google.maps.event.addListener(this.map, 'dblclick', (e) => {
                    this.addressData.address.latitude = e.latLng.lat();
                    this.addressData.address.longitude = e.latLng.lng();
                    let positionDoubleclick = e.latLng;
                    marker.setPosition(positionDoubleclick);
                    this.map.setCenter(positionDoubleclick);
                });
            },
            fillFromZip: debounce(async function (zip) {
                if (zip) {
                    if (!this.addressData.address || !this.addressData.address.country || zip.length < 5) return;

                    try {
                        this.$parent.$emit('busy', true);
                        const { data } = await API.get(
                            `/asynchronous/ziptastic/${this.addressData.address.country.id}/${zip}`
                        );
                        if (!data || data.message === 'Not Found') {
                            throw new Error();
                        }
                        this.addressData.address.city = data.city;
                        this.states.forEach((state) => {
                            if (data.state_short) {
                                if (state.code === data.state_short) {
                                    this.addressData.address.state = state;
                                }
                            } else {
                                if (state.name === data.state) {
                                    this.addressData.address.state = state;
                                }
                            }
                        });
                        this.geoCodeAddressMovePoint();
                    } catch (err) {
                        this.warningToast({
                            text: 'Unable to auto fill city and state.',
                        });
                    } finally {
                        this.$parent.$emit('busy', false);
                    }
                }
            }, 300),
            geoCodeAddressMovePoint() {
                try {
                    let zip = this.addressData.address.zip;
                    let address = this.addressData.address.address1 + '  ' + this.addressData.address.address2;
                    let country = this.addressData.address.country?.name;
                    let state = this.addressData.address.state?.name;
                    let city = this.addressData.address.city;
                    if (country == 'St Martin/Maarten') {
                        country = 'Sint Maarten';
                    }
                    let to_geocode = `${address}" "${city}", "${state}"   "${zip}"   "${country}`;

                    let geocoder = new this.google.maps.Geocoder();

                    geocoder.geocode({ address: to_geocode }, (results, status) => {
                        if (status == this.google.maps.GeocoderStatus.OK) {
                            let point = results[0].geometry.location;
                            this.addressData.address.latitude = point.lat();
                            this.addressData.address.longitude = point.lng();

                            this.deleteMarkers();

                            const marker = new this.google.maps.Marker({
                                zIndex: 1,
                                position: point,
                                map: this.map,
                                draggable: false,
                            });
                            this.markers.push(marker);
                            this.map.panTo(marker.getPosition());
                            this.google.maps.event.addListener(this.map, 'dblclick', (e) => {
                                this.addressData.address.latitude = e.latLng.lat();
                                this.addressData.address.longitude = e.latLng.lng();
                                let positionDoubleclick = e.latLng;
                                marker.setPosition(positionDoubleclick);
                                this.map.setCenter(positionDoubleclick);
                            });
                        } else {
                            console.log('Geocode was not successful for the following reason: ' + status);
                        }
                    });
                } catch (error) {
                    console.log('geoCodeAddressMovePoint error:', error);
                    throw new Error('Geocode was not successful.');
                }
            },
            movePoint() {
                this.deleteMarkers();
                let point = new this.google.maps.LatLng(
                    this.addressData.address.latitude,
                    this.addressData.address.longitude
                );
                this.map.setCenter(point);
                let marker = new this.google.maps.Marker({
                    position: point,
                    map: this.map,
                });
                this.markers.push(marker);
                marker.setMap(this.map);
            },
            async fillAddress(selectedAddress) {
                try {
                    this.$parent.$emit('busy', true);
                    this.selectedAddress = selectedAddress;
                    const { data } = await API.post(
                        `${RENTAL_PROPERTY_SETUP_ADDRESS_ENDPOINT(this.propertyId)}fill-address/`,
                        { property_id: this.selectedAddress }
                    );
                    this.addressData = this.getAddressDataValue(data);
                    this.commitChangesToStore(this.addressData);
                    this.geoCodeAddressMovePoint();
                    this.successToast({ text: 'Address successfully filled.' });
                } catch (e) {
                    this.errorToast({
                        text: e.message || 'Could not fill address.',
                    });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            deleteMarkers() {
                for (var i = 0; i < this.markers.length; i++) {
                    this.markers[i].setMap(null);
                }
                this.markers = [];
            },
            onValidatePhone(value) {
                if (value.valid) {
                    this.addressData.address.phone = value.number;
                }
            },
            setPhone(value) {
                this.addressData.address.phone = value;
            },
            async fetchStates(value) {
                try {
                    this.$parent.$emit('busy', true);
                    const data = await this.$store.dispatch('setupPropertyStore/getStates', { id: value });
                    let found = false;
                    for (let i = 0; i < data.states.length; i++) {
                        if (data.states[i].id == this.addressData.address.state.id) {
                            found = true;
                            break;
                        }
                    }
                    if (!found) this.addressData.address.state = data.states[0];
                } catch (e) {
                    this.errorToast({ text: 'Could not load states.' });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            changeShowNearby() {
                this.showNearby = !this.showNearby;
            },
            changeShowStory() {
                this.showStory = !this.showStory;
            },
            commitChangesToStore(data) {
                this.$store.commit('setupPropertyStore/gotAddress', data);
            },
            setInvalid(value) {
                this.$emit('setInvalid', value);
            },
            setLocations(locations) {
                this.addressData.locations = this.getLocationsValue(locations);
            },
            async save() {
                try {
                    this.$parent.$emit('busy', true);
                    const module = this.getModuleEndpointAndBody();
                    const { data } = await this.$store.dispatch('setupPropertyStore/saveModule', module);
                    this.addressData = this.getAddressDataValue(data.address);
                    this.commitChangesToStore(this.addressData);
                    this.$emit('refetchInitialData');
                    this.successToast({
                        text: 'Property address successfully saved.',
                    });
                } catch (e) {
                    const { generic, fields } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not save address.' });
                    if (!isEmpty(fields)) {
                        this.$refs.address_form.setErrors(fields);
                    }
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
        },
        watch: {
            'addressData.address.country.id': function (countryID, previousCountryID) {
                if (countryID !== previousCountryID) {
                    this.fetchStates(countryID);
                }
            },
            addressesOptions(addresses) {
                if (addresses?.length) this.selectedAddress = addresses[0].value;
            },
            addressData: {
                handler(val) {
                    if (this.$refs.address_form) {
                        this.$refs.address_form.setValues(val);
                    }
                },
                deep: true,
            },
        },
    };
</script>

<style lang="scss" scoped>
    .google-map {
        height: 300px;
        display: block;
        position: relative !important;
    }
    .expandable {
        cursor: pointer;
        &:hover {
            color: #0d47a1;
        }
    }
</style>
